import {
	MatterMortgageWithAssociations,
	ValidationResult,
} from '@models/mortgage';
import {
	MortgageDocumentPackage,
	PackageProcessStatuses,
} from '@models/mortgage-document-packages';
import { STATUSES } from '@models/statuses';
import { Selector } from '@ngxs/store';
import { MatterMortgagePackagesState } from '@state/matter/matter-mortgage-packages';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { addDays, isAfter } from 'date-fns';
import { PackageType } from 'src/app/shared/api-repos/mortgages/closer-sync-instructions.api.service';
import { RequestForFundsSelectors } from '../closer-sync-rff/request-for-funds.selector';
import {
	PACKAGE_STATUS_TYPES,
	PackageStatus,
} from './closer-sync-mortgage.model';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { MatterFile } from '@models/matter-file';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';

const sentStatuses = [
	PackageProcessStatuses.SENT_TO_LENDER_SUCCESS,
] as PackageProcessStatuses[];

const sendingStatuses = [
	PackageProcessStatuses.CREATING,
	PackageProcessStatuses.SENT_TO_LENDER_FAILED,
	PackageProcessStatuses.SUBMITTED,
	PackageProcessStatuses.READY_TO_SUBMIT,
] as PackageProcessStatuses[];

const pendingStatuses = [
	PackageProcessStatuses.LENDER_CONTACTED_AWAITING_CALLBACK,
	PackageProcessStatuses.LENDER_CONTACTED_UPDATE_COMMUNICATED,
	PackageProcessStatuses.LENDER_CONTACTED_PROCESS_NOT_CONFIRMED,
] as PackageProcessStatuses[];

const receivedStatuses = [
	PackageProcessStatuses.LENDER_CONTACTED_PROCESS_CONFIRMED,
] as PackageProcessStatuses[];

export class CloserSyncMortgageSelectors {
	static getPackageStatus(
		pack: MortgageDocumentPackage,
		packageType: PackageType,
		closingDate: string,
		fundingDetailsValidation?: ValidationResult,
	): PackageStatus {
		const afterClosingDate = isAfter(
			new Date(Date.now()),
			addDays(new Date(closingDate), 1),
		);
		const _packageType = packageType?.toLowerCase();

		if (sendingStatuses.includes(pack?.file.status)) {
			return {
				status: PACKAGE_STATUS_TYPES.SENDING,
				createdAt: new Date(pack?.file.createdAt),
				afterClosingDate,
				displayStatus: STATUSES.SENDING,
			};
		}

		if (pendingStatuses.includes(pack?.file.status)) {
			return {
				status: PACKAGE_STATUS_TYPES.SENT,
				createdAt: new Date(pack?.file.createdAt),
				afterClosingDate,
				displayStatus: STATUSES.PENDING_CONFIRMATION,
			};
		}

		if (receivedStatuses.includes(pack?.file.status)) {
			return {
				status: PACKAGE_STATUS_TYPES.CONFIRMED,
				createdAt: new Date(pack?.file.createdAt),
				afterClosingDate,
				displayStatus: CloserSyncMortgageSelectors.getDisplayStatus(
					PACKAGE_STATUS_TYPES.CONFIRMED,
					_packageType,
				),
			};
		}

		if (sentStatuses.includes(pack?.file.status)) {
			return {
				status: PACKAGE_STATUS_TYPES.SENT,
				createdAt: new Date(pack?.file.createdAt),
				afterClosingDate,
				displayStatus: CloserSyncMortgageSelectors.getDisplayStatus(
					PACKAGE_STATUS_TYPES.SENT,
					_packageType,
				),
			};
		}

		if (
			(PackageType.RequestForFunds.toLowerCase() ===
				_packageType.toLowerCase() &&
				fundingDetailsValidation?.isValid) ||
			(!!pack &&
				PackageType.FinalReport.toLowerCase() === _packageType.toLowerCase() &&
				afterClosingDate)
		) {
			return {
				status: PACKAGE_STATUS_TYPES.REVIEW,
				createdAt: null,
				afterClosingDate,
				displayStatus: CloserSyncMortgageSelectors.getDisplayStatus(
					PACKAGE_STATUS_TYPES.REVIEW,
					_packageType,
				),
			};
		}

		return {
			status: PACKAGE_STATUS_TYPES.PREPARE,
			createdAt: null,
			afterClosingDate,
			displayStatus: CloserSyncMortgageSelectors.getDisplayStatus(
				PACKAGE_STATUS_TYPES.PREPARE,
				_packageType,
			),
		};
	}

	static getDisplayStatus(
		packageStatus: PACKAGE_STATUS_TYPES,
		type?: string,
	): STATUSES {
		switch (packageStatus) {
			case PACKAGE_STATUS_TYPES.PREPARE:
				if (type === PackageType.FinalReport.toLowerCase())
					return STATUSES.NOT_SENT;
				return STATUSES.ACTIONS_REQUIRED;
			case PACKAGE_STATUS_TYPES.REVIEW:
				return STATUSES.NOT_SENT;
			case PACKAGE_STATUS_TYPES.SENT:
				return STATUSES.SENT;
			case PACKAGE_STATUS_TYPES.CONFIRMED:
				return STATUSES.RECIEVED_BY_LENDER;
			default:
				return STATUSES.NOT_SENT;
		}
	}

	static getSharedStatus(
		packageType: PackageType,
		packages: MortgageDocumentPackage[],
		closingDate: string,
		fundingDetailsValidation?: ValidationResult,
	): PackageStatus {
		const _packageType = packageType.toLowerCase();
		const packagesOfType = packages?.filter((pack) =>
			pack.packageType
				? pack.packageType?.toLowerCase() === _packageType
				: pack.packageDescription.toLowerCase() === _packageType,
		);
		const mostRecentPackage = packagesOfType
			.filter((pkg) => !pkg.file.supportingDocument)
			.reduce((recent, pkg) => {
				return recent?.file?.createdAt > pkg?.file?.createdAt ? recent : pkg;
			}, null);

		return CloserSyncMortgageSelectors.getPackageStatus(
			mostRecentPackage,
			packageType,
			closingDate,
			fundingDetailsValidation,
		);
	}

	@Selector([
		MatterMortgagePackagesState.getAll,
		SelectedMatter.closingDate,
		RequestForFundsSelectors.ValidateFundingDetails,
	])
	static getRequestForFundsPackageStatus(
		packages: MortgageDocumentPackage[],
		closingDate: string,
		fundingDetailsValidation: ValidationResult,
	): PackageStatus {
		return CloserSyncMortgageSelectors.getSharedStatus(
			PackageType.RequestForFunds,
			packages,
			closingDate,
			fundingDetailsValidation,
		);
	}

	@Selector([MatterMortgagesState.getSelectedMortgage])
	static isLocalBranchSet(mortgage: MatterMortgageWithAssociations): boolean {
		return !!mortgage.fundingDetails?.localBranch;
	}

	@Selector([MatterMortgagePackagesState.getAll, SelectedMatter.closingDate])
	static getFinalReportPackageStatus(
		packages: MortgageDocumentPackage[],
		closingDate: string,
	): PackageStatus {
		return CloserSyncMortgageSelectors.getSharedStatus(
			PackageType.FinalReport,
			packages,
			closingDate,
		);
	}

	@Selector([
		MatterMortgagePackagesState.getAll,
		SelectedMatter.closingDate,
		RequestForFundsSelectors.ValidateFundingDetails,
		MatterUploadedFilesSelectors.getUploadedFilesBySource(
			'package_status_update',
		),
	])
	static getAllPackages(
		packages: MortgageDocumentPackage[],
		closingDate: string,
		fundingDetailsValidation?: ValidationResult,
		faxReceipts?: MatterFile[],
	): MortgageDocumentPackage[] {
		return packages.map((pack) => {
			const faxReceipt = faxReceipts?.find((fax) =>
				fax.systemTags.find((tag) => `package_id:${pack.id}` === tag),
			);
			return {
				...pack,
				faxReceipt,
				displayStatus: CloserSyncMortgageSelectors.getPackageStatus(
					pack,
					(pack.packageType || pack.packageDescription) as PackageType,
					closingDate,
					fundingDetailsValidation,
				).displayStatus,
			};
		});
	}
}
